import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { registerUser } from '../services/authService';
import { auth } from '../firebase';
import { signInWithEmailAndPassword } from 'firebase/auth';
import ThemeLogo from "./ThemeLogo";

export default function Register() {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [form, setForm] = useState({
    firstName: '', lastName: '', email: '', phone: '', street: '', postCode: '', county: '', city: '', password: '', confirmPassword: ''
  });
  const navigate = useNavigate();
  const [showPopup, setShowPopup] = useState(false);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Email validation function using standard regex pattern
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Handle email validation on blur
  const handleEmailBlur = (e) => {
    const email = e.target.value.trim();
    if (email && !validateEmail(email)) {
      setEmailError('Please enter a valid email address.');
    } else {
      setEmailError('');
    }
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    // Clear email error when user starts typing
    if (e.target.name === 'email' && emailError) {
      setEmailError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    
    // Check for email validation error
    if (emailError) {
      setError('Please fix the email validation error before submitting.');
      setIsLoading(false);
      return;
    }
    
    // Validate email format before submission
    if (form.email && !validateEmail(form.email)) {
      setEmailError('Please enter a valid email address.');
      setError('Please fix the email validation error before submitting.');
      setIsLoading(false);
      return;
    }
    
    // Password validation: minimum 8 characters, 1 uppercase, 1 lowercase, 1 digit, 1 special character
    const hasMinLength = form.password.length >= 8;
    const hasUppercase = /[A-Z]/.test(form.password);
    const hasLowercase = /[a-z]/.test(form.password);
    const hasDigit = /\d/.test(form.password);
    const hasSpecialChar = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(form.password);
    
    if (!hasMinLength || !hasUppercase || !hasLowercase || !hasDigit || !hasSpecialChar) {
      setError('Your password must contain at least one uppercase letter, one lowercase letter, one digit, one special character, and be at least 8 characters long.');
      setIsLoading(false);
      return;
    }
    
    if (form.password !== form.confirmPassword) {
      setError('Passwords do not match.');
      setIsLoading(false);
      return;
    }
    
    try {
      await registerUser({
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        password: form.password,
        avatar: '',
        county: form.county,
        city: form.city,
        phone: form.phone,
        postCode: form.postCode,
        streetAddress: form.street,
        subscriptionName: '', // can be set after payment
        subscriptionType: '', // can be set after payment
      });
      // Automatically log in the user after registration
      await signInWithEmailAndPassword(auth, form.email, form.password);
      setShowPopup(true);
      setTimeout(() => {
        setShowPopup(false);
        navigate('/login');
      }, 1500);
    } catch (err) {
      let userMessage = 'Registration failed. Please try again.';
      if (err.code === 'auth/email-already-in-use') {
        userMessage = 'This email is already registered. Please use a different email or login.';
      } else if (err.code === 'auth/invalid-email') {
        userMessage = 'Please enter a valid email address.';
      } else if (err.code === 'auth/weak-password') {
        userMessage = 'Password is too weak. Please use a stronger password.';
      } else if (err.code === 'auth/network-request-failed') {
        userMessage = 'Network error. Please check your connection and try again.';
      }
      setError(userMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      width: '100vw',
      background: 'rgba(0,0,0,0.0)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'fixed',
      top: 0,
      left: 0,
      zIndex: 2000
    }}>
      <div style={{
        background: '#fff',
        borderRadius: '32px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
        padding: '48px 40px 32px 40px',
        maxWidth: 900,
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        position: 'relative',
      }}>
        <span
          onClick={() => navigate('/')}
          style={{
            position: 'absolute',
            top: 18,
            right: 24,
            fontSize: 22,
            color: '#888',
            cursor: 'pointer',
            zIndex: 10,
            fontWeight: 400,
            lineHeight: 1,
            transition: 'color 0.2s',
          }}
          aria-label="Close register"
          title="Close"
        >
          &times;
        </span>
        {showPopup && (
          <Alert variant="success" style={{ position: 'absolute', top: 16, left: 32, right: 32, zIndex: 10, fontWeight: 600, fontSize: '1.1rem', textAlign: 'center' }}>
            You have successfully registered, now you're moving to login page.
          </Alert>
        )}
        {error && (
          <Alert variant="danger" style={{ position: 'absolute', top: 60, left: 32, right: 32, zIndex: 10, fontWeight: 600, fontSize: '1.1rem', textAlign: 'center' }}>
            {error}
          </Alert>
        )}
        <ThemeLogo style={{ width: 130, height: 130, marginBottom: 24 }} alt="Logo" />
        <h2 style={{ fontSize: '2.1rem', fontWeight: 700, margin: 0, color: '#111', alignSelf: 'flex-start' }}>Register</h2>
        <div style={{ width: '100%', margin: '8px 0 24px 0', alignSelf: 'flex-start' }}>
          <span style={{ color: '#222', fontSize: '1.08rem', fontWeight: 500 }}>Already have an account? </span>
          <span style={{ color: '#8b5cf6', fontWeight: 600, cursor: 'pointer', transition: 'color 0.2s' }} 
                onMouseEnter={(e) => e.target.style.color = '#6366f1'} 
                onMouseLeave={(e) => e.target.style.color = '#8b5cf6'} 
                onClick={() => navigate('/login')}>Login Now</span>
        </div>
        <Form style={{ width: '100%' }} onSubmit={handleSubmit}>
          <Container fluid>
            <Row className="g-3">
              <Col md={6}>
                <Form.Control name="firstName" value={form.firstName} onChange={handleChange} placeholder="First Name" style={inputStyle} required />
              </Col>
              <Col md={6}>
                <Form.Control name="lastName" value={form.lastName} onChange={handleChange} placeholder="Last Name" style={inputStyle} required />
              </Col>
              <Col md={6}>
                <Form.Control name="email" value={form.email} onChange={handleChange} placeholder="Email Address" style={inputStyle} required type="email" onBlur={handleEmailBlur} />
                {emailError && (
                  <div style={{ color: '#dc3545', fontSize: '0.875rem', marginTop: '4px', fontWeight: 500 }}>
                    {emailError}
                  </div>
                )}
              </Col>
              <Col md={6}>
                <Form.Control name="phone" value={form.phone} onChange={handleChange} placeholder="Phone Number" style={inputStyle} required />
              </Col>
              <Col md={6}>
                <Form.Control name="street" value={form.street} onChange={handleChange} placeholder="Street Address" style={inputStyle} required />
              </Col>
              <Col md={6}>
                <Form.Control name="postCode" value={form.postCode} onChange={handleChange} placeholder="Post Code" style={inputStyle} required />
              </Col>
              <Col md={6}>
                <Form.Control name="county" value={form.county} onChange={handleChange} placeholder="County" style={inputStyle} required />
              </Col>
              <Col md={6}>
                <Form.Control name="city" value={form.city} onChange={handleChange} placeholder="City" style={inputStyle} required />
              </Col>
              <Col md={6}>
                <div style={{ position: 'relative' }}>
                  <Form.Control name="password" type={showPassword ? 'text' : 'password'} value={form.password} onChange={handleChange} placeholder="Password" style={inputStyle} required />
                  <span onClick={() => setShowPassword(v => !v)} style={eyeStyle} aria-label="Toggle password visibility">
                    {showPassword ? eyeOpen : eyeClosed}
                  </span>
                </div>
              </Col>
              <Col md={6}>
                <div style={{ position: 'relative' }}>
                  <Form.Control name="confirmPassword" type={showConfirmPassword ? 'text' : 'password'} value={form.confirmPassword} onChange={handleChange} placeholder="Confirm Password" style={inputStyle} required />
                  <span onClick={() => setShowConfirmPassword(v => !v)} style={eyeStyle} aria-label="Toggle password visibility">
                    {showConfirmPassword ? eyeOpen : eyeClosed}
                  </span>
                </div>
              </Col>
              <Col xs={12} className="mt-2">
                <Button 
                  type="submit" 
                  disabled={isLoading}
                  style={{ 
                    width: '100%', 
                    background: isLoading ? '#6c757d' : 'linear-gradient(135deg, #6d28d9 0%, #4f46e5 45%, #22d3ee 100%)', 
                    color: '#fff', 
                    fontWeight: 700, 
                    fontSize: '1.18rem', 
                    border: 'none', 
                    borderRadius: '12px', 
                    padding: '16px 0', 
                    margin: '10px 0 0 0', 
                    boxShadow: '0 4px 16px rgba(139, 92, 246, 0.25)', 
                    cursor: isLoading ? 'not-allowed' : 'pointer', 
                    transition: 'all 0.3s ease',
                    opacity: isLoading ? 0.7 : 1
                  }}
                >
                  {isLoading ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        style={{ marginRight: '8px' }}
                      />
                      Registering...
                    </>
                  ) : (
                    'Register'
                  )}
                </Button>
              </Col>
            </Row>
          </Container>
        </Form>
      </div>
    </div>
  );
}

const inputStyle = {
  width: '100%',
  padding: '16px',
  borderRadius: '10px',
  border: 'none',
  background: '#f3f3ff',
  color: '#7d7dbb',
  fontSize: '1.08rem',
  marginBottom: 0,
  outline: 'none',
  fontWeight: 500,
  boxSizing: 'border-box',
};

const eyeStyle = {
  position: 'absolute',
  right: 18,
  top: 18,
  cursor: 'pointer',
  color: '#b0b0c3',
  fontSize: 20,
  userSelect: 'none',
};

const eyeOpen = (
  <svg width="20" height="20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M10 4.5c-4.5 0-7.5 4.5-7.5 4.5s3 4.5 7.5 4.5 7.5-4.5 7.5-4.5-3-4.5-7.5-4.5zm0 7.5a3 3 0 1 1 0-6 3 3 0 0 1 0 6z" fill="#b0b0c3"/></svg>
);
const eyeClosed = (
  <svg width="20" height="20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1.5 10S4.5 5.5 10 5.5s8.5 4.5 8.5 4.5-3 4.5-8.5 4.5S1.5 10 1.5 10zm8.5 2.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z" fill="#b0b0c3"/></svg>
); 